import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const FloatingCreateButton = () => {
    const navigate = useNavigate();
    const location = useLocation();
    
    // Check if we're on the create page
    const isOnCreatePage = location.pathname === '/create';
    
    // Drag and position state
    const [isDragging, setIsDragging] = useState(false);
    const [position, setPosition] = useState(() => {
        // Load saved position from localStorage
        try {
            const saved = localStorage.getItem('createButtonPosition');
            if (saved) {
                const parsed = JSON.parse(saved);
                // Validate the position data structure
                if (parsed && typeof parsed === 'object' && 
                    (parsed.edge === 'left' || parsed.edge === 'bottom') &&
                    typeof parsed.offset === 'number' && 
                    parsed.offset >= 0) {
                    
                    // Clamp offset to viewport bounds on initial load
                    if (typeof window !== 'undefined') {
                        const viewportWidth = window.innerWidth;
                        const viewportHeight = window.innerHeight;
                        const buttonSize = 88; // Button is 88px
                        
                        if (parsed.edge === 'left') {
                            const maxOffset = Math.max(24, viewportHeight - buttonSize - 24);
                            return {
                                edge: parsed.edge,
                                offset: Math.max(24, Math.min(parsed.offset, maxOffset))
                            };
                        } else {
                            const maxOffset = Math.max(24, viewportWidth - buttonSize - 24);
                            return {
                                edge: parsed.edge,
                                offset: Math.max(24, Math.min(parsed.offset, maxOffset))
                            };
                        }
                    }
                    return parsed;
                }
            }
        } catch (e) {
            console.warn('Failed to parse createButtonPosition from localStorage:', e);
        }
        // Default position: left edge, 30% from top + 64px (button height) + 25px spacing (below chef tab)
        return { 
            edge: 'left', 
            offset: typeof window !== 'undefined' 
                ? Math.max(24, Math.round(window.innerHeight * 0.3) + 64 + 25) 
                : 24 
        };
    });
    const dragStartRef = useRef({ x: 0, y: 0, startPos: { edge: 'bottom', offset: 0 } });
    const buttonRef = useRef(null);

    // Save position to localStorage whenever it changes
    useEffect(() => {
        localStorage.setItem('createButtonPosition', JSON.stringify(position));
    }, [position]);

    // Validate and clamp position when window resizes
    useEffect(() => {
        const validatePosition = () => {
            if (typeof window === 'undefined') return;
            
            setPosition(currentPosition => {
                const viewportWidth = window.innerWidth;
                const viewportHeight = window.innerHeight;
                const buttonSize = 88;
                
                let needsUpdate = false;
                let newPosition = { ...currentPosition };
                
                if (currentPosition.edge === 'left') {
                    const maxOffset = Math.max(24, viewportHeight - buttonSize - 24);
                    if (currentPosition.offset > maxOffset) {
                        newPosition.offset = maxOffset;
                        needsUpdate = true;
                    }
                } else {
                    const maxOffset = Math.max(24, viewportWidth - buttonSize - 24);
                    if (currentPosition.offset > maxOffset) {
                        newPosition.offset = maxOffset;
                        needsUpdate = true;
                    }
                }
                
                return needsUpdate ? newPosition : currentPosition;
            });
        };
        
        validatePosition();
        window.addEventListener('resize', validatePosition);
        
        return () => {
            window.removeEventListener('resize', validatePosition);
        };
    }, []);

    // Drag handlers
    const handleMouseDown = (e) => {
        if (e.button !== 0) return; // Only left mouse button
        
        setIsDragging(true);
        dragStartRef.current = {
            x: e.clientX,
            y: e.clientY,
            startPos: { ...position }
        };
        e.preventDefault();
    };

    const handleMouseMove = (e) => {
        if (!isDragging) return;

        const deltaX = e.clientX - dragStartRef.current.x;
        const deltaY = e.clientY - dragStartRef.current.y;
        const moveThreshold = 10;

        if (Math.abs(deltaX) > moveThreshold || Math.abs(deltaY) > moveThreshold) {
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;
            const currentX = e.clientX;
            const currentY = e.clientY;

            const leftDistance = currentX;
            const bottomDistance = viewportHeight - currentY;

            // Snap to left edge if closer to left (within left 30% of screen)
            if (leftDistance < viewportWidth * 0.3 && currentY < viewportHeight - 100) {
                const newOffset = Math.max(24, Math.min(viewportHeight - 88, currentY));
                setPosition({ edge: 'left', offset: newOffset });
            } else {
                const newOffset = Math.max(24, Math.min(viewportWidth - 88, currentX));
                setPosition({ edge: 'bottom', offset: newOffset });
            }
        }
    };

    const handleMouseUp = (e) => {
        if (isDragging) {
            const deltaX = e.clientX - dragStartRef.current.x;
            const deltaY = e.clientY - dragStartRef.current.y;
            const moveThreshold = 10;

            // If didn't move much, treat as click
            if (Math.abs(deltaX) <= moveThreshold && Math.abs(deltaY) <= moveThreshold) {
                if (isOnCreatePage) {
                    navigate(-1); // Go back to previous page
                } else {
                    navigate('/create');
                }
            }
        }
        setIsDragging(false);
    };

    // Add global event listeners for dragging
    useEffect(() => {
        if (isDragging) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
            return () => {
                window.removeEventListener('mousemove', handleMouseMove);
                window.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isDragging, position]);

    // Touch handlers for mobile
    const handleTouchStart = (e) => {
        const touch = e.touches[0];
        setIsDragging(true);
        dragStartRef.current = {
            x: touch.clientX,
            y: touch.clientY,
            startPos: { ...position }
        };
    };

    const handleTouchMove = (e) => {
        if (!isDragging) return;
        
        const touch = e.touches[0];
        const deltaX = touch.clientX - dragStartRef.current.x;
        const deltaY = touch.clientY - dragStartRef.current.y;
        const moveThreshold = 10;

        if (Math.abs(deltaX) > moveThreshold || Math.abs(deltaY) > moveThreshold) {
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;
            const currentX = touch.clientX;
            const currentY = touch.clientY;

            const leftDistance = currentX;

            if (leftDistance < viewportWidth * 0.3 && currentY < viewportHeight - 100) {
                const newOffset = Math.max(24, Math.min(viewportHeight - 88, currentY));
                setPosition({ edge: 'left', offset: newOffset });
            } else {
                const newOffset = Math.max(24, Math.min(viewportWidth - 88, currentX));
                setPosition({ edge: 'bottom', offset: newOffset });
            }
        }
        e.preventDefault();
    };

    const handleTouchEnd = (e) => {
        if (isDragging) {
            const touch = e.changedTouches[0];
            const deltaX = touch.clientX - dragStartRef.current.x;
            const deltaY = touch.clientY - dragStartRef.current.y;
            const moveThreshold = 10;

            if (Math.abs(deltaX) <= moveThreshold && Math.abs(deltaY) <= moveThreshold) {
                if (isOnCreatePage) {
                    navigate(-1); // Go back to previous page
                } else {
                    navigate('/create');
                }
            }
        }
        setIsDragging(false);
    };

    // Get dynamic positioning based on edge and offset
    const getDynamicStyle = () => {
        const bodyPadding = 'var(--spacing-sm)';
        
        let safeOffset = position.offset;
        if (typeof window !== 'undefined') {
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;
            const buttonSize = 88;
            
            if (position.edge === 'left') {
                const maxOffset = Math.max(24, viewportHeight - buttonSize - 24);
                safeOffset = Math.max(24, Math.min(position.offset, maxOffset));
            } else {
                const maxOffset = Math.max(24, viewportWidth - buttonSize - 24);
                safeOffset = Math.max(24, Math.min(position.offset, maxOffset));
            }
        }
        
        if (position.edge === 'left') {
            return {
                left: `calc(0px - ${bodyPadding})`,
                top: `${safeOffset}px`,
                position: 'fixed',
                margin: '0',
                transform: 'translateY(0)',
                zIndex: '9998', // One below chef
                cursor: isDragging ? 'grabbing' : 'grab'
            };
        } else {
            return {
                bottom: `calc(0px - ${bodyPadding})`,
                left: `${safeOffset}px`,
                position: 'fixed',
                margin: '0',
                transform: 'translateY(0)',
                zIndex: '9998', // One below chef
                cursor: isDragging ? 'grabbing' : 'grab'
            };
        }
    };

    return (
        <div 
            ref={buttonRef}
            className="fixed z-[9998]"
            style={getDynamicStyle()}
        >
            <button
                data-walkthrough="create-button"
                onMouseDown={handleMouseDown}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                className={`${
                    position.edge === 'left' ? 'rounded-r-lg w-20 h-16' : 'rounded-t-lg w-16 h-20'
                }`}
                style={{
                    margin: '0 !important',
                    padding: '0 !important',
                    backgroundColor: '#FFFFFF',
                    borderTop: '1px solid #0D0D0D',
                    borderRight: position.edge === 'left' ? '1px solid #0D0D0D' : '1px solid #0D0D0D',
                    borderLeft: position.edge === 'left' ? 'none' : '1px solid #0D0D0D',
                    borderBottom: position.edge === 'left' ? '1px solid #0D0D0D' : 'none',
                    boxShadow: position.edge === 'left' ? '2px 0 2px rgba(0, 0, 0, 0.02)' : '0 -1px 2px rgba(0, 0, 0, 0.02)',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: isDragging ? 'grabbing' : 'grab',
                    opacity: 1,
                    transition: 'all 200ms ease-out'
                }}
                title={isOnCreatePage ? "Go back" : "Create new recipe"}
            >
                <img 
                    src={isOnCreatePage ? "/images/back-arrow.svg" : "/images/plus-icon.svg"}
                    alt={isOnCreatePage ? "Back" : "Create"}
                    style={{
                        width: '32px',
                        height: '32px',
                        objectFit: 'contain',
                        pointerEvents: 'none',
                        transition: 'all 200ms ease-out'
                    }}
                />
            </button>
        </div>
    );
};

export default FloatingCreateButton;

